import {
  Box,
  Flex,
  type MantineSize,
  Text,
  type TextProps,
  Tooltip,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { clsx } from 'clsx'
import { type ComponentType } from 'react'

import {
  type UNSAFE_any,
  type CoreProduct,
  type ParameterID,
} from '@flecto/types'

import {
  BadgeProductBundle,
  type BadgeProductBundlePopoverDefault,
  type BadgeProductBundlePopoverOverrideProps,
} from '../BadgeProductBundle/BadgeProductBundle'
import { BadgeProductInsurance } from '../BadgeProductInsurance/BadgeProductInsurance'
import classes from './styles.module.css'

export type TextProductTitleProps<
  C extends ComponentType<UNSAFE_any> = BadgeProductBundlePopoverDefault,
> = TextProps & {
  product?: CoreProduct
  size?: MantineSize
  wrapperClassName?: string
  withBadgeInsurance?: boolean
  withBadgeBundle?: boolean
  onItemClick?: (itemId: ParameterID) => void
} & BadgeProductBundlePopoverOverrideProps<C>

export function TextProductTitle<
  C extends ComponentType<UNSAFE_any> = BadgeProductBundlePopoverDefault,
>({
  product,
  size,
  popoverComponent,
  popoverProps,
  withBadgeInsurance = true,
  withBadgeBundle = true,
  onItemClick,
  className,
  wrapperClassName,
  ...props
}: TextProductTitleProps<C>) {
  const isMobile = useMediaQuery('(max-width: 768px)')
  return (
    <Flex
      align='center'
      gap='sm'
      wrap={isMobile ? 'wrap' : 'nowrap'}
      className={wrapperClassName}
    >
      <Text
        data-testid='product-title'
        size={size}
        truncate={!isMobile}
        className={clsx(classes.productTitle, className, {
          [classes.productTitleMobile]: isMobile,
        })}
        {...props}
      >
        {product?.title}
      </Text>
      {withBadgeInsurance && product?.insured && (
        <BadgeProductInsurance
          data-testid='badge-product-insurance'
          product={product}
        />
      )}
      {withBadgeBundle && product?.bundle && (
        <Tooltip label='Bundle' withinPortal data-testid='tooltip-bundle'>
          <Box>
            <BadgeProductBundle
              data-testid='badge-product-bundle'
              product={product}
              popoverComponent={popoverComponent}
              popoverProps={popoverProps}
              onItemClick={onItemClick}
            />
          </Box>
        </Tooltip>
      )}
    </Flex>
  )
}
